// iter150-review — Shared unread-count store for the Inbox badge.
//
// One single module-level poller hits /api/inbox/unread-count every 30 s
// (lighter than /inbox/threads and already role-aware on the backend:
//  - admin → sums unreadAdmin across all threads
//  - school → sums unreadSchool across the school's threads).
// The BottomNav badge, the mobile AppHeader bell dot and the desktop
// InboxBell all subscribe to this store so they can never disagree.
//
// Visibility + online gating:
//  - we only tick while document.visibilityState === "visible" AND
//    navigator.onLine === true.
//  - on visibilitychange → visible we tick immediately.
//  - on 'online' we tick immediately.
//
// `refreshUnreadNow()` is exported so inbox screens can call it right
// after opening/replying to a thread, so the badge drops without having
// to wait for the next 30-second poll.
import { useEffect, useState } from "react";
import axios from "axios";
import { TOKEN_KEY } from "@/lib/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30_000;

let unread = 0;
let timerId = null;
const subs = new Set();

function notify() {
  subs.forEach((cb) => { try { cb(unread); } catch { /* ignore */ } });
}

async function tick() {
  // Guard: don't tick if the tab is hidden or we're offline.
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  // Also skip if no auth token (logged-out) — avoid 401 noise.
  try {
    const tok = typeof localStorage !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
    if (!tok) return;
  } catch { /* ignore */ }
  try {
    const r = await axios.get(`${API}/inbox/unread-count`);
    const n = Number(r.data?.unread || 0);
    if (n !== unread) {
      unread = n;
      notify();
    }
  } catch {
    // Silent — network / auth blip is fine.
  }
}

export function refreshUnreadNow() { tick(); }

function ensurePollerStarted() {
  if (timerId !== null || subs.size === 0) return;
  // Kick off an immediate tick, then every 30 s.
  tick();
  timerId = setInterval(tick, POLL_MS);
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibility);
  }
  if (typeof window !== "undefined") {
    window.addEventListener("online", onOnline);
    window.addEventListener("focus", onFocus);
  }
}

function stopPollerIfIdle() {
  if (subs.size === 0 && timerId !== null) {
    clearInterval(timerId);
    timerId = null;
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisibility);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("focus", onFocus);
    }
  }
}

function onVisibility() {
  if (document.visibilityState === "visible") tick();
}
function onOnline() { tick(); }
function onFocus() { tick(); }

export function useUnreadCount() {
  const [n, setN] = useState(unread);
  useEffect(() => {
    const cb = (v) => setN(v);
    subs.add(cb);
    ensurePollerStarted();
    return () => {
      subs.delete(cb);
      stopPollerIfIdle();
    };
  }, []);
  return n;
}

// Called by AuthContext on login/logout so the badge immediately re-syncs
// (or resets to 0 after logout).
export function resetUnreadCount() {
  unread = 0;
  notify();
}
